import { type ComponentType, useRef, useState, useCallback } from "react";
import { UploadSimpleIcon } from "@phosphor-icons/react/UploadSimple";
import { FileTextIcon } from "@phosphor-icons/react/FileText";
import { ImageIcon } from "@phosphor-icons/react/Image";
import { FileIcon } from "@phosphor-icons/react/File";
import { XIcon } from "@phosphor-icons/react/X";
import { toast } from "sonner";
import { Icon } from "../Icons/Icon";
import { formatFileSize } from "../Helpers/formatFileSize";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";

// ── Helpers ───────────────────────────────────────────────────────

function getFileIcon(file: File): ComponentType<{ className?: string }> {
  if (file.type === "application/pdf") return FileTextIcon;
  if (file.type.startsWith("image/")) return ImageIcon;
  return FileIcon;
}

function describeAcceptedTypes(accept?: string): string {
  if (!accept) return "All file types";
  const exts = accept
    .split(",")
    .map((s) => s.trim().replace(".", "").toUpperCase());
  const imageExts = new Set(["PNG", "JPG", "JPEG", "GIF", "WEBP"]);
  const hasImages = exts.some((e) => imageExts.has(e));
  const hasPdf = exts.includes("PDF");
  if (hasPdf && hasImages) return "PDF and image files";
  if (hasPdf) return "PDF files";
  if (hasImages) return "Image files";
  return exts.join(", ") + " files";
}

function filenameFromResponse(response: Response, url: string): string {
  const disposition = response.headers.get("content-disposition");
  if (disposition) {
    const match = disposition.match(
      /filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)["']?/i,
    );
    if (match?.[1]) return decodeURIComponent(match[1].trim());
  }
  const segment = new URL(url).pathname.split("/").pop();
  if (segment?.includes(".")) return segment;
  return "file";
}

function isAcceptedFile(file: File, accept?: string): boolean {
  if (!accept) return true;
  const accepted = accept.split(",").map((s) => s.trim().toLowerCase());
  const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
  const mime = file.type.toLowerCase();
  return accepted.some(
    (a) =>
      a === ext ||
      a === mime ||
      (a.endsWith("/*") && mime.startsWith(a.replace("/*", "/"))),
  );
}

// ── File row ──────────────────────────────────────────────────────

interface FileRowProps_ {
  file: File;
  onRemove: () => void;
}

function FileRow_({ file, onRemove }: FileRowProps_) {
  return (
    <div className="flex items-center gap-2 rounded-md px-3 py-1.5 hover:bg-muted/50">
      <Icon icon={getFileIcon(file)} size="md" color="text-content-faint" />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {file.name}
      </span>
      <span className="shrink-0 text-xs text-content-muted">
        {formatFileSize(file.size)}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="shrink-0 cursor-pointer text-content-faint transition-colors hover:text-red"
      >
        <Icon icon={XIcon} size="sm" />
      </button>
    </div>
  );
}

// ── FileDropZone ──────────────────────────────────────────────────

interface FileDropZoneProps {
  accept?: string;
  multiple?: boolean;
  value?: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  maxSizeMb?: number;
  /**
   * Forwarded to the hidden `<input type="file">`. The drop target is a div, so
   * a suite that attaches a file has to reach the input itself — Cypress's
   * `selectFile` needs a selector for it.
   */
  id?: string | undefined;
}

export function FileDropZone({
  accept,
  multiple = false,
  value = [],
  onChange,
  disabled = false,
  maxSizeMb,
  id,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragCount, setDragCount] = useState(0);
  const markDirty = useMarkDialogDirty();
  const isDragOver = dragCount > 0;
  const hasFiles = value.length > 0;

  const processFiles = useCallback(
    (incoming: FileList | File[]) => {
      const files = Array.from(incoming);
      const valid: File[] = [];
      for (const file of files) {
        if (!isAcceptedFile(file, accept)) {
          toast.warning(
            `"${file.name}" is not accepted. ${describeAcceptedTypes(accept)} only.`,
          );
          continue;
        }
        if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
          toast.warning(`"${file.name}" exceeds ${maxSizeMb} MB limit.`);
          continue;
        }
        valid.push(file);
      }
      if (valid.length === 0) return;
      markDirty?.();
      if (multiple) {
        onChange([...value, ...valid]);
      } else {
        onChange([valid[0]!]);
      }
    },
    [accept, maxSizeMb, multiple, value, onChange, markDirty],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setDragCount((c) => c + 1);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCount((c) => Math.max(0, c - 1));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCount(0);
      if (disabled) return;

      if (e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
        return;
      }

      // Fallback: web-app drops (e.g. Front) provide a URL instead of a File
      const uriList = e.dataTransfer.getData("text/uri-list");
      const urls = uriList
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && !s.startsWith("#"));

      if (urls.length === 0) return;

      const fetchAndProcess = async () => {
        const toastId = toast.loading("Fetching file…");
        try {
          const files: File[] = [];
          for (const url of urls) {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const name = filenameFromResponse(response, url);
            files.push(new File([blob], name, { type: blob.type }));
          }
          toast.dismiss(toastId);
          processFiles(files);
        } catch {
          toast.dismiss(toastId);
          toast.error(
            "Could not fetch the dragged file. Try downloading it first.",
          );
        }
      };

      void fetchAndProcess();
    },
    [disabled, processFiles],
  );

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
      // Reset so the same file can be selected again
      e.target.value = "";
    },
    [processFiles],
  );

  const handleRemove = useCallback(
    (index: number) => {
      markDirty?.();
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange, markDirty],
  );

  const borderClass = isDragOver
    ? "border-dashed border-2 border-blue/50"
    : hasFiles
      ? "border-2 border-border"
      : "border-dashed border-2 border-border";

  const bgClass = isDragOver ? "bg-blue/5" : "bg-card";

  return (
    <div
      className={`${borderClass} ${bgClass} w-full rounded-md transition-colors ${disabled ? "pointer-events-none opacity-40" : "cursor-pointer hover:border-foreground/30 active:bg-muted/30"}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleClick();
      }}
      role="button"
      tabIndex={0}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
      />

      {hasFiles ? (
        <div className="flex flex-col gap-0.5 py-1.5">
          {value.map((file, i) => (
            <FileRow_
              key={`${file.name}-${file.size}-${file.lastModified}`}
              file={file}
              onRemove={() => handleRemove(i)}
            />
          ))}
          {multiple && (
            <div className="px-3 py-1 text-center text-xs text-content-muted">
              Drop more files or click to add
            </div>
          )}
        </div>
      ) : (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 py-6">
          <Icon
            icon={UploadSimpleIcon}
            size="xl"
            color={isDragOver ? "text-blue-text/50" : "text-content-faint"}
          />
          <span
            className={`text-sm font-medium ${isDragOver ? "text-blue-text/50" : "text-content-secondary"}`}
          >
            Drag & drop or click to browse
          </span>
          <span className="text-xs text-content-muted">
            {describeAcceptedTypes(accept)}
          </span>
        </div>
      )}
    </div>
  );
}
